<template>
	<div v-if="stacked" class="d-flex flex-column ga-4">
		<div v-if="free || canAddFreeLine" class="d-flex align-center flex-wrap ga-2">
			<v-chip v-if="free" color="success" variant="flat" size="small">{{ t('PromoFree') }}</v-chip>
			<v-btn v-if="canAddFreeLine" prepend-icon="add_circle" size="small" variant="outlined" color="success" @click="ops.addFree(offerRef, productId)">{{ t('PromoAddFree') }}</v-btn>
		</div>
		<div>
			<div class="text-body-small text-medium-emphasis mb-1">{{ t('VolumeNego') }}</div>
			<div class="d-flex align-center ga-2">
				<v-text-field :key="`volume-${resetKey}`" :model-value="row?.volume ?? 0" type="number" min="0" inputmode="numeric" variant="outlined" density="compact" hide-details :disabled="!editable" class="flex-grow-1" @change="(event) => update('volume', event.target.value)" />
				<v-select :menu-props="menuProps" :model-value="row?.productContainerId || 0" :items="containerItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!editable" class="flex-grow-1" @update:model-value="(value) => update('container', value)" />
				<v-chip size="small" color="primary" label>{{ format.uc(volumeUC) }}</v-chip>
			</div>
			<div v-if="preco.volumeUC" class="text-body-small text-medium-emphasis mt-1">{{ t('VolumePreco') }} {{ format.uc(preco.volumeUC) }}</div>
		</div>
		<v-select :menu-props="menuProps" :model-value="row?.locationPromoId || 0" :items="locationItems" item-title="label" item-value="id" :label="t('Location')" variant="outlined" density="compact" hide-details :disabled="!editable" @update:model-value="(value) => update('location', value)" />
		<v-select :menu-props="menuProps" :model-value="row?.locationHighlightId || 0" :items="highlightItems" item-title="label" item-value="id" :label="t('LocationHighlight')" variant="outlined" density="compact" hide-details :disabled="!editable" @update:model-value="(value) => update('highlight', value)" />
		<div class="d-flex align-center flex-wrap ga-3">
			<v-badge v-if="!free" :model-value="additionalCount > 0" :content="additionalCount" color="warning">
				<v-btn :prepend-icon="additionalAction.icon" :disabled="!editable && !additionalCount" @click="openAdditional">{{ t(additionalAction.label) }}</v-btn>
			</v-badge>
			<v-spacer />
			<div class="text-end">
				<div class="text-body-small text-medium-emphasis">{{ free ? t('PromoFree') : `${t('ProductPrice')} ${format.money(price)}` }}</div>
				<div class="text-body-medium font-weight-bold">{{ free ? format.money(0) : volumeUC > 0 ? format.money(value) : '—' }}</div>
			</div>
			<v-btn v-if="orderable" icon="shopping_cart" variant="tonal" color="primary" @click="ops.orderLine(offerRef, productId, free)" />
		</div>
		<v-alert v-for="(error, index) in errors" :key="index" type="error" variant="tonal" density="compact">{{ formatPromoMessage(error.display, t) }}</v-alert>
	</div>
	<tr v-else>
		<td class="position-relative">
			<v-sheet v-if="status" :color="status" width="4" class="position-absolute top-0 bottom-0 left-0" />
			<div class="d-flex align-center ga-1 py-2 ps-4 pe-2">
				<v-btn v-if="free" icon="delete" size="x-small" variant="text" color="error" :disabled="!editable" @click="ops.removeFree(offerRef, productId)" />
				<template v-else>
					<v-btn v-if="offerRef.offerKey && !snapshot.readonly" icon="delete" size="x-small" variant="text" color="error" @click="ops.removeLocalProduct(offerRef, productId)" />
					<v-btn v-if="canAddFreeLine" icon="add_circle" size="x-small" variant="text" color="success" :title="t('PromoAddFree')" @click="ops.addFree(offerRef, productId)" />
				</template>
				<v-chip v-if="free" color="success" variant="flat" size="small">{{ product?.label }}</v-chip>
				<ProductLabel v-else :params="{ value: product?.label || '', subtitle: product?.ean, imageThumbnailUid: product?.imageThumbnailUid, productId, avatarSize: 32 }" />
			</div>
			<div v-for="(error, index) in errors" :key="index" class="text-body-small text-error ps-4 pe-2 pb-2">{{ formatPromoMessage(error.display, t) }}</div>
		</td>
		<td>
			<div class="d-flex align-center ga-2 pa-2">
				<v-text-field :key="`volume-${resetKey}`" :model-value="row?.volume ?? 0" type="number" min="0" variant="outlined" density="compact" hide-details :disabled="!editable" width="84" @change="(event) => update('volume', event.target.value)" />
				<v-select :menu-props="menuProps" :model-value="row?.productContainerId || 0" :items="containerItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!editable" width="120" @update:model-value="(value) => update('container', value)" />
				<v-chip size="small" color="primary" label>{{ format.uc(volumeUC) }}</v-chip>
			</div>
			<div v-if="preco.volumeUC" class="text-body-small text-medium-emphasis px-2 pb-2">{{ t('VolumePreco') }} {{ format.uc(preco.volumeUC) }}</div>
		</td>
		<td>
			<div class="pa-2">
				<v-select :menu-props="menuProps" :model-value="row?.locationPromoId || 0" :items="locationItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!editable" width="160" @update:model-value="(value) => update('location', value)" />
			</div>
		</td>
		<td>
			<div class="pa-2">
				<v-select :menu-props="menuProps" :model-value="row?.locationHighlightId || 0" :items="highlightItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!editable" width="140" @update:model-value="(value) => update('highlight', value)" />
			</div>
		</td>
		<td>
			<div class="pa-2">
				<v-badge :model-value="additionalCount > 0" :content="additionalCount" color="warning">
					<v-btn :prepend-icon="additionalAction.icon" size="small" :disabled="free || (!editable && !additionalCount)" @click="openAdditional">{{ t(additionalAction.label) }}</v-btn>
				</v-badge>
			</div>
		</td>
		<td class="text-end">
			<div v-if="free" class="pa-2">
				<div class="text-body-small text-medium-emphasis">{{ t('PromoFree') }}</div>
				<div class="font-weight-bold text-medium-emphasis">{{ format.money(0) }}</div>
			</div>
			<div v-else class="pa-2">
				<div class="text-body-small text-medium-emphasis">{{ t('ProductPrice') }} {{ format.money(price) }}</div>
				<div class="font-weight-bold">{{ volumeUC > 0 ? format.money(value) : '—' }}</div>
			</div>
		</td>
		<td>
			<div class="pa-2">
				<v-btn v-if="orderable" icon="shopping_cart" size="small" variant="text" color="primary" @click="ops.orderLine(offerRef, productId, free)" />
			</div>
		</td>
	</tr>
	<PromoAdditionalPresenceDialog v-model="additionalOpen" :campaign="campaign" :offer="offer" :offer-ref="offerRef" :product-id="productId" kind="nego" :editable="editable" />
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { NEGO, containersOf, getProduct, negoRowsOf } from '@/utils/domain/promo/promoModel';
import { isProductEditable, negotiatedRowOf } from '@/utils/domain/promo/promoNego';
import { canAddFree, freeRowOf } from '@/utils/domain/promo/promoFree';
import { additionalOf, canOpenAdditional } from '@/utils/domain/promo/promoAdditional';
import { productStatus } from '@/utils/domain/promo/promoStatus';
import { priceOf, productNegoValue, productPreco } from '@/utils/domain/promo/promoValues';
import { isLineOrderable } from '@/utils/domain/promo/promoOrder';
import { formatPromoMessage, productErrors } from '@/utils/domain/promo/promoValidation';
import { sumOf } from '@/utils/domain/promo/promoPrimitives';
import ProductLabel from '@/components/common/ProductLabel.vue';
import PromoAdditionalPresenceDialog from './PromoAdditionalPresenceDialog.vue';

const props = defineProps({
	campaign: { type: Object, required: true },
	offer: { type: Object, required: true },
	offerRef: { type: Object, required: true },
	productId: { type: Number, required: true },
	free: { type: Boolean, default: false },
	stacked: { type: Boolean, default: false },
});

const { snapshot, validation, ops, format, notify, t } = usePromoStep();
const additionalOpen = ref(false);
const resetKey = ref(0);
const overlayTarget = inject('overlayTarget', null);
const menuProps = { attach: overlayTarget || undefined };

const product = computed(() => getProduct(snapshot.value, props.productId));
const row = computed(() => (props.free ? freeRowOf(snapshot.value, props.offerRef, props.productId) : negotiatedRowOf(snapshot.value, props.offerRef, props.productId)));
const editable = computed(() => isProductEditable(snapshot.value, props.campaign, props.offerRef, props.productId));
const status = computed(() => productStatus(snapshot.value, props.campaign, props.offer.id, props.productId, props.offerRef.offerKey, props.free));
const additional = computed(() => (props.free ? [] : additionalOf(snapshot.value, props.offerRef, props.productId, NEGO)));
const additionalCount = computed(() => additional.value.length);
const additionalAction = computed(() => (editable.value ? { icon: 'add', label: 'PromoAddPresence' } : { icon: 'add_location_alt', label: 'AdditionnalPresence' }));
const volumeUC = computed(() => (Number(row.value?.volumeUC) || 0) + (sumOf(additional.value, 'volumeUC') || 0));
const price = computed(() => priceOf(props.campaign, props.productId));
const value = computed(() => productNegoValue(negoRowsOf(snapshot.value, props.offerRef), props.offerRef, props.productId));
const preco = computed(() => productPreco(negoRowsOf(snapshot.value, props.offerRef), props.offerRef, props.productId));
const canAddFreeLine = computed(() => !props.free && !freeRowOf(snapshot.value, props.offerRef, props.productId) && canAddFree(snapshot.value, props.offerRef, props.productId));
const orderable = computed(() => isLineOrderable(snapshot.value, props.offerRef, props.productId, props.free));
const errors = computed(() => productErrors(validation.value, props.offerRef, props.productId, props.free).filter((e) => e.display));

const empty = computed(() => ({ id: 0, label: '...' }));
const containerItems = computed(() => [empty.value, ...containersOf(snapshot.value, props.offer, props.productId)]);
const locationItems = computed(() => [empty.value, ...snapshot.value.refs.locations]);
const highlightItems = computed(() => [empty.value, ...snapshot.value.refs.highlights]);

function update(field, value) {
	const change = { ...props.offerRef, productId: props.productId, field, value };
	if (!(props.free ? ops.free(change) : ops.nego(change))) resetKey.value += 1;
}

function openAdditional() {
	const check = editable.value ? canOpenAdditional(snapshot.value, props.offerRef, props.productId, NEGO) : { ok: true };
	if (!check.ok) return notify(check.error);
	additionalOpen.value = true;
}
</script>
